import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  fetchGlossaryTerms,
  fetchGlossaryTags,
  createGlossaryTerm,
  updateGlossaryTerm,
  deleteGlossaryTerm,
  setSearchQuery,
  setSelectedTags,
  clearSearch,
  clearError
} from '@/store/slices/glossarySlice';
import { GlossaryTermWithRelations, CreateGlossaryTermRequest, UpdateGlossaryTermRequest } from '@shared/types/glossary';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Tag, 
  Database, 
  FileText,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createGlossaryTermSchema, updateGlossaryTermSchema } from '@shared/schemas/glossary';
import { cn } from '@/lib/utils';

import { z } from 'zod';
type TermFormData = z.infer<typeof createGlossaryTermSchema>;
type UpdateFormData = z.infer<typeof updateGlossaryTermSchema>;

const GlossaryManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    terms: rawTerms, 
    loading, 
    error, 
    searchQuery, 
    selectedTags, 
    availableTags,
    isCreating,
    isUpdating,
    isDeleting
  } = useAppSelector(state => state.glossary);

  // Use terms directly from state
  const terms = rawTerms || [];

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTermWithRelations | null>(null);
  const [deletingTermId, setDeletingTermId] = useState<string | null>(null);

  // Forms
  const createForm = useForm({
    resolver: zodResolver(createGlossaryTermSchema),
    defaultValues: {
      term: '',
      description: '',
      technicalDescription: '',
      tags: [],
      aliases: [],
      relatedTermIds: []
    }
  });

  const updateForm = useForm<UpdateFormData>({
    resolver: zodResolver(updateGlossaryTermSchema),
    defaultValues: {}
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchGlossaryTerms({ limit: 100 }));
    dispatch(fetchGlossaryTags());
  }, [dispatch]);

  // Search effect with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchGlossaryTerms({ 
        search: searchQuery, 
        tags: selectedTags || [],
        limit: 100 
      }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedTags, dispatch]);

  // Clear error when component unmounts or success
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleCreateSubmit = async (data: z.infer<typeof createGlossaryTermSchema>) => {
    try {
      await dispatch(createGlossaryTerm(data)).unwrap();
      setIsCreateOpen(false);
      createForm.reset();
    } catch (error) {
      // Error handled by Redux
    }
  };

  const handleUpdateSubmit = async (data: UpdateFormData) => {
    if (!editingTerm) return;
    
    try {
      await dispatch(updateGlossaryTerm({ 
        id: editingTerm.id, 
        data 
      })).unwrap();
      setEditingTerm(null);
      updateForm.reset();
    } catch (error) {
      // Error handled by Redux
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingTermId(id);
    try {
      await dispatch(deleteGlossaryTerm(id)).unwrap();
    } catch (error) {
      // Error handled by Redux
    } finally {
      setDeletingTermId(null);
    }
  };

  const handleEdit = (term: GlossaryTermWithRelations) => {
    setEditingTerm(term);
    updateForm.reset({
      term: term.term,
      description: term.description,
      technicalDescription: term.technicalDescription || '',
      tags: term.tags,
      aliases: term.aliases,
      relatedTermIds: term.relatedTerms.map((t: any) => t.id)
    });
  };


  const TermForm: React.FC<{
    form: any;
    onSubmit: (data: any) => void;
    isLoading: boolean;
    submitText: string;
  }> = ({ form, onSubmit, isLoading, submitText }) => {
    const { register, handleSubmit, setValue, watch, formState: { errors } } = form;
    const watchedTags = watch('tags') || [];
    const watchedAliases = watch('aliases') || [];
    
    // Local state for input fields to prevent focus loss
    const [tagInput, setTagInput] = useState('');
    const [aliasInput, setAliasInput] = useState('');
    const [selectedRelatedTerms, setSelectedRelatedTerms] = useState<string[]>([]);

    // Update selectedRelatedTerms when form data changes
    useEffect(() => {
      const relatedIds = watch('relatedTermIds') || [];
      setSelectedRelatedTerms(relatedIds);
    }, [watch]);

    const addTag = () => {
      if (tagInput.trim() && !watchedTags.includes(tagInput.trim())) {
        const newTags = [...watchedTags, tagInput.trim()];
        setValue('tags', newTags);
        setTagInput('');
      }
    };

    const removeTag = (tagToRemove: string) => {
      setValue('tags', watchedTags.filter((tag: string) => tag !== tagToRemove));
    };

    const addAlias = () => {
      if (aliasInput.trim() && !watchedAliases.includes(aliasInput.trim())) {
        const newAliases = [...watchedAliases, aliasInput.trim()];
        setValue('aliases', newAliases);
        setAliasInput('');
      }
    };

    const removeAlias = (aliasToRemove: string) => {
      setValue('aliases', watchedAliases.filter((alias: string) => alias !== aliasToRemove));
    };

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Term */}
        <div className="space-y-2">
          <Label htmlFor="term">Term *</Label>
          <Input
            id="term"
            {...register('term')}
            placeholder="e.g., IPO"
          />
          {errors.term && (
            <div className="text-sm text-red-600">{errors.term.message}</div>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Business-friendly explanation of the term"
            rows={3}
          />
          {errors.description && (
            <div className="text-sm text-red-600">{errors.description.message}</div>
          )}
        </div>

        {/* Technical Description */}
        <div className="space-y-2">
          <Label htmlFor="technicalDescription">Technical Description</Label>
          <Textarea
            id="technicalDescription"
            {...register('technicalDescription')}
            placeholder="Database fields, table references, etc."
            rows={2}
          />
          {errors.technicalDescription && (
            <div className="text-sm text-red-600">{errors.technicalDescription.message}</div>
          )}
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label>Tags</Label>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add a tag"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addTag}
              disabled={!tagInput.trim()}
            >
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {watchedTags.map((tag: string) => (
              <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                {tag}
                <X 
                  className="h-3 w-3 cursor-pointer hover:text-red-600" 
                  onClick={() => removeTag(tag)}
                />
              </Badge>
            ))}
          </div>
        </div>

        {/* Aliases */}
        <div className="space-y-2">
          <Label>Aliases</Label>
          <div className="flex gap-2">
            <Input
              value={aliasInput}
              onChange={(e) => setAliasInput(e.target.value)}
              placeholder="Add an alias"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAlias();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addAlias}
              disabled={!aliasInput.trim()}
            >
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {watchedAliases.map((alias: string) => (
              <Badge key={alias} variant="outline" className="flex items-center gap-1">
                {alias}
                <X 
                  className="h-3 w-3 cursor-pointer hover:text-red-600" 
                  onClick={() => removeAlias(alias)}
                />
              </Badge>
            ))}
          </div>
        </div>

        {/* Related Terms */}
        <div className="space-y-2">
          <Label>Related Terms</Label>
          <div className="max-h-32 overflow-y-auto space-y-1 border rounded p-2">
            {terms.map(term => (
              <label key={term.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedRelatedTerms.includes(term.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newIds = [...selectedRelatedTerms, term.id];
                      setSelectedRelatedTerms(newIds);
                      setValue('relatedTermIds', newIds);
                    } else {
                      const newIds = selectedRelatedTerms.filter(id => id !== term.id);
                      setSelectedRelatedTerms(newIds);
                      setValue('relatedTermIds', newIds);
                    }
                  }}
                />
                {term.term}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : submitText}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Glossary Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage project terminology and definitions
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Term
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Glossary Term</DialogTitle>
              <DialogDescription>
                Add a new term to the project glossary with its definition, technical details, and related information.
              </DialogDescription>
            </DialogHeader>
            <TermForm
              form={createForm}
              onSubmit={handleCreateSubmit}
              isLoading={isCreating}
              submitText="Create Term"
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-800">{error}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch(clearError())}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search terms..."
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="pl-10"
              />
            </div>
            {(searchQuery || (selectedTags && selectedTags.length > 0)) && (
              <Button
                variant="outline"
                onClick={() => dispatch(clearSearch())}
              >
                Clear
              </Button>
            )}
          </div>

          {availableTags && availableTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-foreground self-center">Filter by tags:</span>
              {availableTags && availableTags.map((tag: string) => (
                <Badge
                  key={tag}
                  variant={selectedTags && selectedTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    if (selectedTags && selectedTags.includes(tag)) {
                      dispatch(setSelectedTags(selectedTags.filter(t => t !== tag)));
                    } else {
                      dispatch(setSelectedTags([...(selectedTags || []), tag]));
                    }
                  }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terms List */}
      <div className="space-y-4">
        {loading && terms.length === 0 ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : terms.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-muted-foreground">No glossary terms found</div>
              <div className="text-sm text-muted-foreground/70 mt-1">
                {searchQuery || (selectedTags && selectedTags.length > 0)
                  ? "Try adjusting your search criteria"
                  : "Create your first glossary term to get started"
                }
              </div>
            </CardContent>
          </Card>
        ) : (
          terms.map(term => (
            <Card key={term.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl">{term.term}</CardTitle>
                    <p className="text-foreground mt-2">{term.description}</p>
                    
                    {term.technicalDescription && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <Database className="h-4 w-4" />
                          Technical Details
                        </div>
                        <p className="text-sm text-foreground font-mono bg-muted p-2 rounded mt-1">
                          {term.technicalDescription}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-4 mt-4 text-sm">
                      {term.tags.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <div className="flex gap-1">
                            {term.tags.map((tag: string) => (
                              <Badge key={tag} variant="secondary">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {term.aliases.length > 0 && (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>Aliases: {term.aliases.join(', ')}</span>
                        </div>
                      )}
                    </div>

                    {term.relatedTerms.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="text-muted-foreground">Related: </span>
                        {term.relatedTerms.map((related: any, idx: number) => (
                          <span key={related.id}>
                            {idx > 0 && ', '}
                            <span className="text-blue-600">{related.term}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(term)}
                      disabled={isUpdating}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(term.id)}
                      disabled={deletingTermId === term.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {deletingTermId === term.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTerm} onOpenChange={() => setEditingTerm(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Glossary Term</DialogTitle>
            <DialogDescription>
              Modify the term definition, technical details, tags, aliases, and related terms.
            </DialogDescription>
          </DialogHeader>
          {editingTerm && (
            <TermForm
              form={updateForm}
              onSubmit={handleUpdateSubmit}
              isLoading={isUpdating}
              submitText="Update Term"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GlossaryManagementPage;